import type { ReactNode } from 'react';
import { EmptyState } from '@/shared/components';
import type { PropertyGroupResponse } from '@/shared/api';
import { ResourceCalendarSection } from '@/features/resources';
import { ApartmentsCard } from './ApartmentsCard';
import type { CalendarToolbarProps } from './CalendarToolbar';
import type { CalendarReassignmentRequest } from './calendarReassignment';
import { ParkingCalendarSection } from './ParkingCalendarSection';
import type { useApartmentCalendar } from './useApartmentCalendar';
import { useCalendarRows } from './useCalendarRows';
import type { GuestAccess } from './bookingDetailsModel';
import type { CalendarView } from './useCalendarView';

interface Props {
  view: CalendarView;
  group: PropertyGroupResponse | null;
  calendar: ReturnType<typeof useApartmentCalendar>;
  dates: string[];
  today: string;
  toolbar: Omit<CalendarToolbarProps, 'layout'>;
  editable: boolean;
  operations: boolean;
  /** Gastdaten wie der Server: Name und Telefon mit Kontakt-Recht, E-Mail mit vollen Gastdaten. */
  guestAccess: GuestAccess;
  onFreeDay: (propertyId: string, date: string, propertyName: string) => void;
  onReassignmentRequest: (request: CalendarReassignmentRequest) => void;
}

const card = 'bg-card border-hairline rounded-tile shadow-float border';

/** Die Karte unter dem Kopf: je Modus Apartments, Stellplätze oder Ressourcen. */
export function CalendarBody(props: Props) {
  const { view, group, calendar } = props;
  const rows = useCalendarRows(calendar.data, {
    selectedProperties: props.toolbar.selectedProperties,
    enabledChannels: view.enabledChannels,
    stayFilter: view.stayFilter,
    today: props.today,
  });
  if (!group) {
    return (
      <Card>
        <EmptyState
          icon="Building"
          title="Objekt auswählen"
          description="Wähle oben in der Navigation genau ein Objekt für die Belegung aus."
        />
      </Card>
    );
  }
  if (view.mode === 'parking') {
    return (
      <Card>
        <ParkingCalendarSection
          group={group}
          dates={props.dates}
          today={props.today}
          density={view.density}
        />
      </Card>
    );
  }
  if (view.mode === 'resources') {
    return (
      <Card>
        <ResourceCalendarSection
          groupId={group.id}
          dates={props.dates}
          today={props.today}
          canManage={props.editable}
          day={view.search.day}
          onDay={(day) => view.patch({ day })}
        />
      </Card>
    );
  }
  return (
    <ApartmentsCard
      view={view}
      calendar={calendar}
      rows={rows}
      toolbar={props.toolbar}
      today={props.today}
      editable={props.editable}
      operations={props.operations}
      guestAccess={props.guestAccess}
      onFreeDay={props.onFreeDay}
      onReassignmentRequest={props.onReassignmentRequest}
    />
  );
}

function Card({ children }: { children: ReactNode }) {
  return <section className={`${card} p-4 md:p-5`}>{children}</section>;
}
